// The GitLab half of a connected webhook source (plan 101 slice 07): the project a
// `gitlab` source observes, and the hint its ingest mapper hands the worker.
//
// READ-ONLY, on gitlab.com or jihulab.com ONLY (`GITLAB_INSTANCES`). A `gitlab` source's deliveries file no raw task: the
// mapper enqueues a HINT naming the merge request, and the worker re-reads it through
// the GitLab REST client (`@developerz/gitlab`) with the source's own API token, grades
// it, and writes one `forge.mr.observed` audit row. The payload is never trusted for
// state; it only says which MR to look at.
//
// Domain may not import `@developerz/gitlab` (it imports nothing internal), so the
// shapes here are restated by name, not derived. `@developerz/gitlab` returns its ids
// with `instance` = the API host, the same form as {@link GitlabInstanceSchema}.

import { z } from 'zod';
import { AccountIdSchema, StoredRepoIdSchema, WebhookSourceIdSchema } from '../value-objects/ids';
import type { PullRequestState } from './issue';

/**
 * The GitLab instances a source may name: each one's HOST, the stored `instance` form
 * (`github.com` for GitHub; `packages/gitlab/src/ids.ts`). A CLOSED allowlist of two
 * fixed public SaaS hosts: gitlab.com, and jihulab.com (JiHu, the China-operated GitLab
 * SaaS; GitLab 19.5 with Standard Webhooks signing tokens, reachable from the cluster,
 * owner ruling 2026-10-10).
 *
 * DECISION #3986 still holds: no caller-supplied host is ever reached from inside the
 * cluster, so a connect naming any host outside this list is refused
 * (`instance_unsupported`). Self-hosted GitLab stays out of scope until slice 08's
 * runner relay. A member is a STORED value (`webhook_sources.forge_project`,
 * `forge.mr.observed`): adding one is safe for existing rows, removing one is not.
 */
export const GITLAB_INSTANCES = ['gitlab.com', 'jihulab.com'] as const;

/** One of {@link GITLAB_INSTANCES}, compared as written (no case or port folding). */
export const GitlabInstanceSchema = z.enum(GITLAB_INSTANCES);
export type GitlabInstance = z.infer<typeof GitlabInstanceSchema>;

/** The instance a connect that names none observes: the default, unchanged since slice 07. */
export const GITLAB_COM_INSTANCE = 'gitlab.com' satisfies GitlabInstance;

/**
 * The v4 REST base of an allowlisted instance, in the canonical spelling
 * `parseBaseUrl` (`@developerz/gitlab`) requires. The ONE place a base URL is built
 * from a stored `instance`: the api's connect read and the worker's re-read both
 * call it, so the host a token is sent to is always a member of the closed set.
 */
export function gitlabApiBaseUrl(instance: GitlabInstance): string {
  return `https://${instance}/api/v4`;
}

/** One path segment: GitLab's own alphabet, never `.` / `..` (they rewrite a URL path). */
const PATH_SEGMENT = /^[A-Za-z0-9_.][A-Za-z0-9_.-]*$/;

/**
 * A project's `path_with_namespace` (`gitlab-org/gitlab`, `group/sub/project`): at
 * least namespace + project, each segment in GitLab's path alphabet. It is display and
 * routing data only — every read addresses the project by `projectId`.
 */
export const GitlabProjectPathSchema = z
  .string()
  .min(3)
  .max(255)
  .refine((p) => {
    const segments = p.split('/');
    return (
      segments.length >= 2 && segments.every((s) => PATH_SEGMENT.test(s) && s !== '.' && s !== '..')
    );
  }, 'expected a GitLab path_with_namespace such as "group/project"');

/**
 * `webhook_sources.forge_project`: the project a `gitlab` source observes. Present
 * exactly on a `gitlab` row (`WebhookSourceSchema`). Filled by the connect leg from
 * ONE read of the project made with the source's own token (which proves the token and
 * resolves the numeric id), never from a caller's say-so.
 *
 * A stored shape: a field added later must be optional or arrive with a backfill.
 */
export const ForgeProjectSchema = z.strictObject({
  /** The instance host, one of {@link GITLAB_INSTANCES}. */
  instance: GitlabInstanceSchema,
  /** GitLab's numeric project id, unique on `instance` only. */
  projectId: z.number().int().positive(),
  /** `path_with_namespace` as read at connect. A rename leaves this stale; `projectId` is the key. */
  pathWithNamespace: GitlabProjectPathSchema,
});
export type ForgeProject = z.infer<typeof ForgeProjectSchema>;

/**
 * The job the ingest mapper enqueues for an accepted GitLab delivery (merge request,
 * pipeline or note event) and the worker consumes, on `GITLAB_MR_HINT_QUEUE`
 * (`@developerz/queue`). A HINT: it names which merge request to re-read, never what
 * state it is in.
 *
 * `accountId` rides along so the worker's source read is account-fenced; it is copied
 * from the source row at ingest, never from the payload. `projectId` is the delivery's
 * `project.id`, already checked by ingest against the source's `forge_project`; the
 * worker checks it again against the row it loads.
 */
export const GitlabMrHintSchema = z.strictObject({
  sourceId: WebhookSourceIdSchema,
  accountId: AccountIdSchema,
  projectId: z.number().int().positive(),
  /** The MR's per-project `iid` (`!123`), the number the REST paths take. */
  mrIid: z.number().int().positive(),
  /** The authenticated `webhook-id`: the delivery's idempotency key, same across retries. */
  deliveryId: z.string().min(1).max(200),
});
export type GitlabMrHint = z.infer<typeof GitlabMrHintSchema>;

/**
 * The job the GitLab MR-hint lane enqueues after an observation that resolved a repo row, and
 * the GitLab policy ingest consumes (`GITLAB_POLICY_INGEST_QUEUE`, `@developerz/queue`): re-read
 * the project's committed `.dz/maintainer/maintainer.yml` through the source that observed it.
 * Ids only — the worker loads the source account-fenced and proves the repo is the account's.
 */
export const GitlabPolicyIngestSchema = z.strictObject({
  sourceId: WebhookSourceIdSchema,
  accountId: AccountIdSchema,
  projectId: z.number().int().positive(),
  /** The `repositories` row, as stored: a uuid (#7931). */
  repoId: StoredRepoIdSchema,
});
export type GitlabPolicyIngest = z.infer<typeof GitlabPolicyIngestSchema>;

/** GitLab's `state` on a merge request, as `mergeRequestSchema` reads it. */
export type GitlabMergeRequestState = 'opened' | 'closed' | 'locked' | 'merged';

/**
 * GitLab's four merge-request states as the three this column stores.
 *
 * `locked` -> `open`, and that is the fail-CLOSED direction rather than the obvious reading:
 * GitLab's `locked` is a transient state while a merge is in progress, so the merge request
 * is neither closed nor merged. A row recorded as `closed` is one babysit stops watching, and
 * stopping on a merge request that is still open loses the work; carrying an in-progress one
 * as open costs one more observation.
 *
 * `pull_requests.state` has THREE values (`PullRequestState`), so `merged` maps on its own
 * and must not be folded into `closed` either. Exhaustive over the type: the wire boundary
 * (`mergeRequestSchema.state`) is an enum, so a fifth value would fail there first.
 */
export function pullRequestStateOfGitlab(state: GitlabMergeRequestState): PullRequestState {
  switch (state) {
    case 'opened':
    case 'locked':
      return 'open';
    case 'merged':
      return 'merged';
    case 'closed':
      return 'closed';
  }
}
